import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { companyService } from '../../services'
import { useState, useCallback, useMemo } from 'react'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { Badge } from '../../components/ui/badge'

const SALES_STATUS_KEY_MAP: Record<string, string> = {
  proposal_sent: 'proposalSent',
  contract_signed: 'contractSigned',
  active_customer: 'activeCustomer',
}

const SALES_STATUS_OPTIONS = [
  'lead',
  'qualified',
  'proposal_sent',
  'negotiation',
  'contract_signed',
  'active_customer',
  'lost',
] as const

function getSalesStatusVariant(status: string): 'default' | 'success' | 'warning' | 'error' {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
    lead: 'default',
    qualified: 'default',
    proposal_sent: 'warning',
    negotiation: 'warning',
    contract_signed: 'success',
    active_customer: 'success',
    lost: 'error',
  }
  return variants[status] || 'default'
}

export default function CompanyCrmPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)

  const getSalesStatusLabel = useCallback(
    (status: string) => {
      const key = SALES_STATUS_KEY_MAP[status] || status
      return t(`crm.salesStatusValues.${key}`)
    },
    [t]
  )

  const salesStatusOptions = useMemo(
    () =>
      SALES_STATUS_OPTIONS.map(value => ({
        value,
        label: getSalesStatusLabel(value),
      })),
    [getSalesStatusLabel]
  )

  const interestLevelOptions = useMemo(
    () => [
      { value: '', label: t('common.noSelection') },
      { value: 'low', label: t('crm.interestLevelValues.low') },
      { value: 'medium', label: t('crm.interestLevelValues.medium') },
      { value: 'high', label: t('crm.interestLevelValues.high') },
    ],
    [t]
  )

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companyService.getCompanyById(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => companyService.updateCompany(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      setIsEditing(false)
    },
  })

  const [formData, setFormData] = useState({
    salesStatus: company?.salesStatus || 'lead',
    contactPersonName: company?.contactPersonName || '',
    contactPersonEmail: company?.contactPersonEmail || '',
    contactPersonPhone: company?.contactPersonPhone || '',
    interestLevel: company?.interestLevel || '',
    proposalSentDate: company?.proposalSentDate
      ? new Date(company.proposalSentDate).toISOString().split('T')[0]
      : '',
    expectedCloseDate: company?.expectedCloseDate
      ? new Date(company.expectedCloseDate).toISOString().split('T')[0]
      : '',
    estimatedContractValue: company?.estimatedContractValue || '',
    leadSource: company?.leadSource || '',
    lastContactDate: company?.lastContactDate
      ? new Date(company.lastContactDate).toISOString().split('T')[0]
      : '',
    notes: company?.notes || '',
    contractSignedDate: company?.contractSignedDate
      ? new Date(company.contractSignedDate).toISOString().split('T')[0]
      : '',
    assignedSalesPerson: company?.assignedSalesPerson || '',
  })

  if (company && !isEditing && formData.salesStatus === 'lead' && company.salesStatus !== 'lead') {
    setFormData({
      salesStatus: company.salesStatus || 'lead',
      contactPersonName: company.contactPersonName || '',
      contactPersonEmail: company.contactPersonEmail || '',
      contactPersonPhone: company.contactPersonPhone || '',
      interestLevel: company.interestLevel || '',
      proposalSentDate: company.proposalSentDate
        ? new Date(company.proposalSentDate).toISOString().split('T')[0]
        : '',
      expectedCloseDate: company.expectedCloseDate
        ? new Date(company.expectedCloseDate).toISOString().split('T')[0]
        : '',
      estimatedContractValue: company.estimatedContractValue?.toString() || '',
      leadSource: company.leadSource || '',
      lastContactDate: company.lastContactDate
        ? new Date(company.lastContactDate).toISOString().split('T')[0]
        : '',
      notes: company.notes || '',
      contractSignedDate: company.contractSignedDate
        ? new Date(company.contractSignedDate).toISOString().split('T')[0]
        : '',
      assignedSalesPerson: company.assignedSalesPerson || '',
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const valueStr =
      typeof formData.estimatedContractValue === 'string'
        ? formData.estimatedContractValue
        : String(formData.estimatedContractValue)
    updateMutation.mutate({
      ...formData,
      estimatedContractValue: valueStr ? parseFloat(valueStr) : undefined,
    })
  }

  const resetForm = () => {
    if (!company) return
    setFormData({
      salesStatus: company.salesStatus || 'lead',
      contactPersonName: company.contactPersonName || '',
      contactPersonEmail: company.contactPersonEmail || '',
      contactPersonPhone: company.contactPersonPhone || '',
      interestLevel: company.interestLevel || '',
      proposalSentDate: company.proposalSentDate
        ? new Date(company.proposalSentDate).toISOString().split('T')[0]
        : '',
      expectedCloseDate: company.expectedCloseDate
        ? new Date(company.expectedCloseDate).toISOString().split('T')[0]
        : '',
      estimatedContractValue: company.estimatedContractValue?.toString() || '',
      leadSource: company.leadSource || '',
      lastContactDate: company.lastContactDate
        ? new Date(company.lastContactDate).toISOString().split('T')[0]
        : '',
      notes: company.notes || '',
      contractSignedDate: company.contractSignedDate
        ? new Date(company.contractSignedDate).toISOString().split('T')[0]
        : '',
      assignedSalesPerson: company.assignedSalesPerson || '',
    })
  }

  if (!id) return <div>{t('errors.validationError')}</div>

  return (
    <PageLayout
      headerProps={{
        title: company?.name || t('crm.loading'),
        subtitle: t('crm.subtitle'),
        backTo: '/companies',
        variant: 'simple',
      }}
      isLoading={isLoading}
      error={error}
      loadingMessage={t('crm.loadingCompanyData')}
      errorMessage={t('crm.loadError')}
    >
      <Container>
        <div className='mx-auto'>
          <div className='bg-white rounded-xl shadow-sm p-6 mb-6'>
            <div className='flex items-start justify-between mb-4'>
              <div>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>{company?.name}</h1>
                {company?.description && <p className='text-gray-600'>{company.description}</p>}
              </div>
              <Badge
                title={getSalesStatusLabel(company?.salesStatus || 'lead')}
                variant={getSalesStatusVariant(company?.salesStatus || 'lead')}
              />
            </div>

            <div className='flex gap-3'>
              <Link
                to={`/companies/${id}/edit`}
                className='px-4 py-2 bg-blue-200 text-blue-800 hover:bg-blue-300 rounded-lg font-medium transition-colors'
              >
                {t('crm.editBasic')}
              </Link>
              <Link
                to={`/companies/${id}/users`}
                className='px-4 py-2 bg-green-200 text-green-800 hover:bg-green-300 rounded-lg font-medium transition-colors'
              >
                {t('crm.manageUsers')}
              </Link>
            </div>
          </div>

          <form onSubmit={handleSubmit} className='bg-white rounded-xl shadow-sm p-6'>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>{t('crm.salesInformation')}</h2>
              {!isEditing ? (
                <button
                  type='button'
                  onClick={() => setIsEditing(true)}
                  className='px-4 py-2 bg-purple-200 text-purple-800 hover:bg-purple-300 rounded-lg font-medium transition-colors'
                >
                  {t('common.edit')}
                </button>
              ) : (
                <div className='flex gap-2'>
                  <button
                    type='button'
                    onClick={() => {
                      setIsEditing(false)
                      resetForm()
                    }}
                    className='px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg font-medium transition-colors'
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type='submit'
                    disabled={updateMutation.isPending}
                    className='px-4 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-lg font-medium transition-colors disabled:opacity-50'
                  >
                    {updateMutation.isPending ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              )}
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.salesStatus')}
                </label>
                <select
                  value={formData.salesStatus}
                  onChange={e => setFormData({ ...formData, salesStatus: e.target.value as any })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                >
                  {salesStatusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.interestLevel')}
                </label>
                <select
                  value={formData.interestLevel}
                  onChange={e => setFormData({ ...formData, interestLevel: e.target.value as any })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                >
                  {interestLevelOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.contactPersonName')}
                </label>
                <input
                  type='text'
                  value={formData.contactPersonName}
                  onChange={e => setFormData({ ...formData, contactPersonName: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.contactPersonEmail')}
                </label>
                <input
                  type='email'
                  value={formData.contactPersonEmail}
                  onChange={e => setFormData({ ...formData, contactPersonEmail: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.contactPersonPhone')}
                </label>
                <input
                  type='tel'
                  value={formData.contactPersonPhone}
                  onChange={e => setFormData({ ...formData, contactPersonPhone: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.assignedSalesPerson')}
                </label>
                <input
                  type='text'
                  value={formData.assignedSalesPerson}
                  onChange={e => setFormData({ ...formData, assignedSalesPerson: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.leadSource')}
                </label>
                <input
                  type='text'
                  value={formData.leadSource}
                  onChange={e => setFormData({ ...formData, leadSource: e.target.value })}
                  disabled={!isEditing}
                  placeholder={t('crm.leadSourcePlaceholder')}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.estimatedContractValue')}
                </label>
                <input
                  type='number'
                  step='0.01'
                  value={formData.estimatedContractValue}
                  onChange={e =>
                    setFormData({ ...formData, estimatedContractValue: e.target.value })
                  }
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.proposalSentDate')}
                </label>
                <input
                  type='date'
                  value={formData.proposalSentDate}
                  onChange={e => setFormData({ ...formData, proposalSentDate: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.expectedCloseDate')}
                </label>
                <input
                  type='date'
                  value={formData.expectedCloseDate}
                  onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.lastContactDate')}
                </label>
                <input
                  type='date'
                  value={formData.lastContactDate}
                  onChange={e => setFormData({ ...formData, lastContactDate: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {t('crm.contractSignedDate')}
                </label>
                <input
                  type='date'
                  value={formData.contractSignedDate}
                  onChange={e => setFormData({ ...formData, contractSignedDate: e.target.value })}
                  disabled={!isEditing}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
                />
              </div>
            </div>

            <div className='mt-6'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('common.notes')}
              </label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                disabled={!isEditing}
                rows={6}
                placeholder={t('crm.notesPlaceholder')}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
              />
            </div>

            {updateMutation.isError && (
              <div className='mt-4 p-4 bg-red-50 border border-red-200 rounded-lg'>
                <p className='text-red-800'>
                  {t('errors.generic')}{' '}
                  {(updateMutation.error as Error)?.message || t('common.unknown')}
                </p>
              </div>
            )}
          </form>

          <div className='mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6'>
            <h3 className='font-semibold text-blue-900 mb-2'>{t('crm.salesHintsTitle')}</h3>
            <ul className='text-sm text-blue-800 space-y-1'>
              <li>• {t('crm.salesHint1')}</li>
              <li>• {t('crm.salesHint2')}</li>
              <li>• {t('crm.salesHint3')}</li>
              <li>• {t('crm.salesHint4')}</li>
            </ul>
          </div>
        </div>
      </Container>
    </PageLayout>
  )
}
